import { useState } from 'react'
import type { CaseEndpoint, EndpointRelationship } from '../../../types/case-container'

export interface ContextMenu<T = CaseEndpoint> {
  x: number
  y: number
  endpoint: T
}

export interface ModalsState {
  showCreateModal: boolean
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>
  showImportDropdown: boolean
  setShowImportDropdown: React.Dispatch<React.SetStateAction<boolean>>
  showCsvImportModal: boolean
  setShowCsvImportModal: React.Dispatch<React.SetStateAction<boolean>>
  showJsonImportModal: boolean
  setShowJsonImportModal: React.Dispatch<React.SetStateAction<boolean>>
  showNewImportModal: boolean
  setShowNewImportModal: React.Dispatch<React.SetStateAction<boolean>>
  showRelationshipImportModal: boolean
  setShowRelationshipImportModal: React.Dispatch<React.SetStateAction<boolean>>
  editingEndpoint: CaseEndpoint | null
  setEditingEndpoint: React.Dispatch<React.SetStateAction<CaseEndpoint | null>>
  linkingEndpoint: CaseEndpoint | null
  setLinkingEndpoint: React.Dispatch<React.SetStateAction<CaseEndpoint | null>>
  viewLinksEndpoint: CaseEndpoint | null
  setViewLinksEndpoint: React.Dispatch<React.SetStateAction<CaseEndpoint | null>>
  editingRelationship: EndpointRelationship | null
  setEditingRelationship: React.Dispatch<React.SetStateAction<EndpointRelationship | null>>
  bulkLinkSourceEndpoint: CaseEndpoint | null
  setBulkLinkSourceEndpoint: React.Dispatch<React.SetStateAction<CaseEndpoint | null>>
  copyLinksTargetEndpoint: CaseEndpoint | null
  setCopyLinksTargetEndpoint: React.Dispatch<React.SetStateAction<CaseEndpoint | null>>
  copyingLinks: boolean
  setCopyingLinks: React.Dispatch<React.SetStateAction<boolean>>
  copyError: string | null
  setCopyError: React.Dispatch<React.SetStateAction<string | null>>
  movingRelationship: boolean
  setMovingRelationship: React.Dispatch<React.SetStateAction<boolean>>
  contextMenu: ContextMenu | null
  setContextMenu: React.Dispatch<React.SetStateAction<ContextMenu | null>>
  mapContextMenu: ContextMenu | null
  setMapContextMenu: React.Dispatch<React.SetStateAction<ContextMenu | null>>
  showViewMapSetModal: boolean
  setShowViewMapSetModal: React.Dispatch<React.SetStateAction<boolean>>
  collectionContextMenu: ContextMenu | null
  setCollectionContextMenu: React.Dispatch<React.SetStateAction<ContextMenu | null>>
  showViewCollectionSetModal: boolean
  setShowViewCollectionSetModal: React.Dispatch<React.SetStateAction<boolean>>
  deleteContextMenu: ContextMenu | null
  setDeleteContextMenu: React.Dispatch<React.SetStateAction<ContextMenu | null>>
  deleteConfirmation: { endpoints: CaseEndpoint[] } | null
  setDeleteConfirmation: React.Dispatch<React.SetStateAction<{ endpoints: CaseEndpoint[] } | null>>
  bulkDeleting: boolean
  setBulkDeleting: React.Dispatch<React.SetStateAction<boolean>>
  viewMode: 'list' | 'map'
  setViewMode: React.Dispatch<React.SetStateAction<'list' | 'map'>>
}

interface ImportModalsState {
  showCreateModal: boolean
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>
  showImportDropdown: boolean
  setShowImportDropdown: React.Dispatch<React.SetStateAction<boolean>>
  showCsvImportModal: boolean
  setShowCsvImportModal: React.Dispatch<React.SetStateAction<boolean>>
  showJsonImportModal: boolean
  setShowJsonImportModal: React.Dispatch<React.SetStateAction<boolean>>
  showNewImportModal: boolean
  setShowNewImportModal: React.Dispatch<React.SetStateAction<boolean>>
  showRelationshipImportModal: boolean
  setShowRelationshipImportModal: React.Dispatch<React.SetStateAction<boolean>>
}

function useImportModalsState(): ImportModalsState {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showImportDropdown, setShowImportDropdown] = useState(false)
  const [showCsvImportModal, setShowCsvImportModal] = useState(false)
  const [showJsonImportModal, setShowJsonImportModal] = useState(false)
  const [showNewImportModal, setShowNewImportModal] = useState(false)
  const [showRelationshipImportModal, setShowRelationshipImportModal] = useState(false)
  return {
    showCreateModal,
    setShowCreateModal,
    showImportDropdown,
    setShowImportDropdown,
    showCsvImportModal,
    setShowCsvImportModal,
    showJsonImportModal,
    setShowJsonImportModal,
    showNewImportModal,
    setShowNewImportModal,
    showRelationshipImportModal,
    setShowRelationshipImportModal,
  }
}

export function useCaseInfrastructureModals(): ModalsState {
  const importState = useImportModalsState()
  const [editingEndpoint, setEditingEndpoint] = useState<CaseEndpoint | null>(null)
  const [linkingEndpoint, setLinkingEndpoint] = useState<CaseEndpoint | null>(null)
  const [viewLinksEndpoint, setViewLinksEndpoint] = useState<CaseEndpoint | null>(null)
  const [editingRelationship, setEditingRelationship] = useState<EndpointRelationship | null>(null)
  const [bulkLinkSourceEndpoint, setBulkLinkSourceEndpoint] = useState<CaseEndpoint | null>(null)
  const [copyLinksTargetEndpoint, setCopyLinksTargetEndpoint] = useState<CaseEndpoint | null>(null)
  const [copyingLinks, setCopyingLinks] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const [movingRelationship, setMovingRelationship] = useState(false)
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null)
  const [mapContextMenu, setMapContextMenu] = useState<ContextMenu | null>(null)
  const [showViewMapSetModal, setShowViewMapSetModal] = useState(false)
  const [collectionContextMenu, setCollectionContextMenu] = useState<ContextMenu | null>(null)
  const [showViewCollectionSetModal, setShowViewCollectionSetModal] = useState(false)
  const [deleteContextMenu, setDeleteContextMenu] = useState<ContextMenu | null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    endpoints: CaseEndpoint[]
  } | null>(null)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list')

  return {
    ...importState,
    editingEndpoint,
    setEditingEndpoint,
    linkingEndpoint,
    setLinkingEndpoint,
    viewLinksEndpoint,
    setViewLinksEndpoint,
    editingRelationship,
    setEditingRelationship,
    bulkLinkSourceEndpoint,
    setBulkLinkSourceEndpoint,
    copyLinksTargetEndpoint,
    setCopyLinksTargetEndpoint,
    copyingLinks,
    setCopyingLinks,
    copyError,
    setCopyError,
    movingRelationship,
    setMovingRelationship,
    contextMenu,
    setContextMenu,
    mapContextMenu,
    setMapContextMenu,
    showViewMapSetModal,
    setShowViewMapSetModal,
    collectionContextMenu,
    setCollectionContextMenu,
    showViewCollectionSetModal,
    setShowViewCollectionSetModal,
    deleteContextMenu,
    setDeleteContextMenu,
    deleteConfirmation,
    setDeleteConfirmation,
    bulkDeleting,
    setBulkDeleting,
    viewMode,
    setViewMode,
  }
}
